/**
 * The buyer's order-history cell. Pure state: the collection-group listener
 * and the per-event name join that fill it are web's own reconciler
 * (`web/src/storefront/orders/orders.sync.ts`).
 *
 * One cell, three settled phases. An empty result is `ready` with no rows —
 * never `error`: a buyer who bought nothing in this browser is not a
 * failure, and the surface's whole difficulty is saying so honestly.
 */
import { state } from "@mhmo91/schmancy/state";
import type { Order } from "@momo/types";

/** One row. `status` comes straight off the order document; count and total
 *  are derived from its own lines. `eventName` is the one field the document
 *  does not carry (order.entity.ts stores no event copy) and is resolved
 *  against the event the order's own path names — `null` while that read is
 *  still in flight, so a row renders before its event answers. */
export type BoughtOrder = Pick<Order.Order, "status"> & {
	readonly orderId: string;
	/** Non-null here: an order with no token has nothing to open, and the
	 *  reconciler drops it rather than rendering a dead row. */
	readonly orderAccessToken: string;
	readonly eventName: string | null;
	readonly boughtAt: Date;
	readonly ticketCount: number;
	readonly totalCents: number;
};

export type OrderHistoryState = {
	readonly status: "loading" | "ready" | "error";
	readonly orders: readonly BoughtOrder[];
};

export const orderHistory = state<OrderHistoryState>(
	"storefront/order-history",
).memory({
	status: "loading",
	orders: [],
});

/** The entry's gate: a cache of the last answer the query settled on, kept in
 *  local storage so a returning buyer's entry is drawn at first paint instead
 *  of one round-trip later. It is never a second source of truth — the
 *  reconciler's `ready` frame is its only writer, since a `loading` or
 *  `error` frame is no evidence that this browser bought nothing. Deliberately
 *  no `keepOnClear`: `clearStates()` then wipes it on the uid transition that
 *  already makes those orders unreadable, so the cache's lifetime is the uid's
 *  by construction rather than by a rule someone has to remember. */
export const hasOrders = state<boolean>("storefront/has-orders").local(false);
